import { Options as HighchartsOptions } from 'highcharts';

import {
  AVERAGE_HANDLING_TIME_CHART,
  AVERAGE_HANDLING_TIME_TITLE,
  CONTACT_VOLUME_CHART,
  CONTACT_VOLUME_TITLE,
  STAFFING_REQUIREMENTS_CHART,
  STAFFING_REQUIREMENTS_TITLE,
} from './constants';
import {
  Adjustment as AdjustmentGql,
  CalculatedForecastData,
  CreateForecastMutationVariables,
  Forecast,
  ForecastInfoInput,
  UpdateForecastMutationVariables,
} from '../../graphql/components';
import { Values } from '../../types';
import { HOURS_24 } from '../Scheduling/config';

export type Adjustment = Omit<AdjustmentGql, '__typename'>;

export const AdjustmentChange = {
  CREATE: 'create',
  DELETE: 'delete',
  EDIT: 'edit',
} as const;

export type AdjustmentChangeType = Values<typeof AdjustmentChange>;

export const adjustmentInChangeLabelKey = {
  [AdjustmentChange.CREATE]: 'Create',
  [AdjustmentChange.DELETE]: 'Delete',
  [AdjustmentChange.EDIT]: 'Edit',
};

export interface CreateForecastState {
  step: number;
  adjustments: Adjustment[];
  adjustmentsUpdated: boolean;
  forecastCreatedUpdated: boolean;
  parameters: ForecastInfoInput;
  parametersChanged: boolean;
  firstStepFormChanged: boolean;
  forecastId: Forecast['id'] | null;
  isCreate: boolean;
  isEdit: boolean;
  isView: boolean;
  isProduction: boolean;
  loading: boolean;
  chartsTimezone: string | undefined;
  queueTimezone: string | undefined;
}

export const ADJUSTMENTS_CONSTRAINS = {
  averageHandlingTime: {
    default: 100,
    min: 0,
    max: 1000,
  },
  contactVolume: {
    default: 100,
    min: 0,
    max: 1000,
  },
  shrinkage: {
    default: 10,
    min: 0,
    max: 95,
  },
  concurrentChats: {
    default: 1,
    min: 1,
    max: 99,
  },
};

export const defaultForecastInfoVariables: ForecastInfoInput = {
  author: 'Guest',
  forecastRange: {
    endDateTime: '',
    startDateTime: '',
  },
  historyRange: {
    endDateTime: '',
    startDateTime: '',
  },
  name: '',
  notes: '',
  queueId: '',
  concurrentChatsCount: null,
};

export const defaultCreateForecastProps: CreateForecastMutationVariables = {
  forecast: {
    adjustments: [],
    info: defaultForecastInfoVariables,
  },
};
export const defaultUpdateForecastVariables: UpdateForecastMutationVariables = {
  forecast: {
    adjustments: [],
    info: defaultForecastInfoVariables,
  },
  id: '0',
};

export const defaultState: CreateForecastState = {
  step: 0,
  adjustments: [],
  adjustmentsUpdated: false,
  parameters: {
    ...defaultForecastInfoVariables,
  },
  parametersChanged: false,
  firstStepFormChanged: false,
  forecastId: null,
  forecastCreatedUpdated: false,
  isCreate: false,
  isEdit: false,
  isView: false,
  isProduction: false,
  loading: false,
  chartsTimezone: undefined,
  queueTimezone: undefined,
};

export const ACTIVE_PLOT_BAND_COLOR = 'rgba(53, 100, 25, 0.1)';
export const DEFAULT_FONT_SIZE = '0.875rem';
export const DEFAULT_FONT_COLOR = '#1a1c1e';
export const DEFAULT_SERIES_COLOR = '#00a8e2';
export const SERIES_IN_COLLISION_IN_PREVIEW_COLOR = 'rgba(237, 109, 79, 0.1)';
export const DEFAULT_SERIES_FILL_COLOR = 'transparent';
export const SERIES_IN_COLLISION_COLOR = 'rgba(237, 109, 79, 0.3)';
export const SERIES_IN_PREVIEW_COLOR = 'rgba(35, 158, 216, 0.2)';
export const AXIS_LINE_COLOR = 'rgb(230, 230, 230)';

export const BASE_SERIES_ID = 'base-series';
export const GAPS_SERIES_ID = 'gaps-series';

export const ChartTypes = {
  SINGLE: 'single',
  COMPOSED: 'composed',
} as const;

export interface ChartTitle {
  className: string;
  dataKey: string;
  dataTestID: string;
}

export type ChartMappedProp = keyof Omit<CalculatedForecastData, '__typename'>;
export interface CommonChartEntry {
  mappedProp: ChartMappedProp;
}

export type ComposedChartEntry = CommonChartEntry & {
  color: string;
  name: string;
};

export type IChartConfig =
  | {
      dataTestID: string;
      chartProps: CommonChartEntry & {
        xAxisDataKey: string;
        nameKey: string;
        yAxisLabel: string;
      };
      refKey: string;
      title: ChartTitle;
      type: typeof ChartTypes.SINGLE;
    }
  | {
      dataTestID: string;
      chartProps: {
        entries: Record<string, ComposedChartEntry>;
        xAxisDataKey: string;
        yAxisLabel: string;
      };
      key: string;
      refKey: string;
      title: ChartTitle;
      type: typeof ChartTypes.COMPOSED;
    };

const xAxisDataKey = 'time';

export const FORECAST_CHART_TYPE = {
  averageHandlingTime: 'averageHandlingTime',
  contactVolume: 'contactVolume',
  staffingAndWorkloadRequirements: 'staffingAndWorkloadRequirements',
} as const;

export const createForecastChartsConfig: Record<Values<typeof FORECAST_CHART_TYPE>, IChartConfig> = {
  [FORECAST_CHART_TYPE.contactVolume]: {
    type: ChartTypes.SINGLE,
    dataTestID: CONTACT_VOLUME_CHART,
    refKey: FORECAST_CHART_TYPE.contactVolume,
    title: {
      className: 'forecasting-studio__create-forecast__chart-headline',
      dataKey: 'forecast.forecastedContactVolume',
      dataTestID: CONTACT_VOLUME_TITLE,
    },
    chartProps: {
      xAxisDataKey,
      mappedProp: 'contactVolume',
      nameKey: 'forecast.forecastedContactVolume',
      yAxisLabel: 'forecast.contacts',
    },
  },
  [FORECAST_CHART_TYPE.averageHandlingTime]: {
    type: ChartTypes.SINGLE,
    dataTestID: AVERAGE_HANDLING_TIME_CHART,
    refKey: FORECAST_CHART_TYPE.averageHandlingTime,
    title: {
      className: 'forecasting-studio__create-forecast__chart-headline',
      dataKey: 'forecast.forecastedAHTFullName',
      dataTestID: AVERAGE_HANDLING_TIME_TITLE,
    },
    chartProps: {
      xAxisDataKey,
      mappedProp: 'averageHandlingTime',
      nameKey: 'forecast.forecastedAHT',
      yAxisLabel: 'time.seconds',
    },
  },
  [FORECAST_CHART_TYPE.staffingAndWorkloadRequirements]: {
    type: ChartTypes.COMPOSED,
    dataTestID: `${STAFFING_REQUIREMENTS_CHART}-composed`,
    title: {
      className: 'forecasting-studio__create-forecast__chart-headline',
      dataKey: 'forecast.staffingRequirements',
      dataTestID: STAFFING_REQUIREMENTS_TITLE,
    },
    refKey: FORECAST_CHART_TYPE.staffingAndWorkloadRequirements,
    chartProps: {
      xAxisDataKey,
      entries: {
        workload: {
          color: '#00a8e2',
          mappedProp: 'workloadRequirements',
          name: 'forecast.baseRequirement',
        },
        staffing: {
          color: 'green',
          mappedProp: 'staffingRequirements',
          name: 'forecast.staffWithShrinkageApplied',
        },
      },
      yAxisLabel: 'person.people',
    },
    key: 'staffing-requirements-composed',
  },
};

export const staticChartOptions: HighchartsOptions = {
  boost: {
    enabled: true,
    useGPUTranslations: true,
  },
  credits: {
    enabled: false,
  },
  chart: {
    height: 372,
    spacing: [34, 40, 15, 13],
    style: {
      fontFamily: 'Roboto, sans-serif',
    },
    zoomType: 'x',
  },
  legend: {
    enabled: false,
  },
  series: [
    {
      color: DEFAULT_SERIES_COLOR,
      connectNulls: false,
      events: {
        mouseOver: () => false,
        mouseOut: () => false,
      },
      lineWidth: 2,
      marker: {
        enabled: false,
        lineWidth: 1,
        radius: 4,
        symbol: 'circle',
      },
      states: {
        inactive: {
          opacity: 1,
        },
        hover: {
          lineWidthPlus: 0,
        },
      },
      type: 'line',
    },
  ],
  title: {
    text: '',
  },
  xAxis: {
    crosshair: true,
    labels: {
      align: 'center',
      autoRotation: [-90],
      autoRotationLimit: 50,
      overflow: 'allow',
      padding: 10,
      reserveSpace: false,
      style: {
        fontSize: DEFAULT_FONT_SIZE,
      },
      y: 50,
    },
    lineColor: '#e6e6e6',
    minPadding: 0,
    maxPadding: 0,
    showEmpty: false,
    tickColor: AXIS_LINE_COLOR,
    tickInterval: HOURS_24,
    tickWidth: 2,
    title: {
      margin: 89,
      style: {
        fontSize: DEFAULT_FONT_SIZE,
        fontWeight: 'bold',
        color: DEFAULT_FONT_COLOR,
      },
    },
    type: 'datetime',
  },
  tooltip: {
    backgroundColor: 'white',
    borderColor: 'transparent',
    followPointer: true,
    hideDelay: 50,
    shape: 'square',
    shared: true,
    style: {
      fontSize: DEFAULT_FONT_SIZE,
    },
    useHTML: true,
  },
  yAxis: {
    labels: {
      style: {
        fontSize: DEFAULT_FONT_SIZE,
        zIndex: '0',
      },
      zIndex: 0,
    },
    showEmpty: false,
    tickAmount: 6,
    title: {
      margin: 25,
      style: {
        fontSize: DEFAULT_FONT_SIZE,
        fontWeight: 'bold',
        color: DEFAULT_FONT_COLOR,
      },
    },
  },
};
